import React, { useEffect, useState, useCallback } from 'react';
import accessLocalStorage from '../../services/accessLocalStorage';
import { extractExtension, isWebEndpoint } from './utils';
import './config-styles.css';

const getApiBase = async () => {
  const envBase = process.env.REACT_APP_CALLCENTER_V2_BASE_URL;
  if (envBase) return envBase;
  const { ipRequest } = await accessLocalStorage.getUserLocalStorage();
  const ipClean = ipRequest.split('/')[2];
  const host = ipClean.split(':')[0];
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  return `${protocol}//${host}`;
};

const getTenantId = async () => {
  const { user: { idtenant } } = await accessLocalStorage.getUserLocalStorage();
  return String(idtenant);
};

const getToken = async () => {
  const { token } = await accessLocalStorage.getUserLocalStorage();
  return token;
};

const formatDuration = (seconds) => {
  if (!seconds || seconds < 0) return '0s';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  if (mins === 0) return `${secs}s`;
  return `${mins}m ${secs}s`;
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const formatTime = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

function History() {
  const [loading, setLoading] = useState(false);
  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(0);
  const [queues, setQueues] = useState([]);
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);
  const limit = 50;

  const today = new Date().toISOString().split('T')[0];
  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  const [filters, setFilters] = useState({
    startDate: weekAgo,
    endDate: today,
    queue: '',
    agent: '',
    disposition: '',
  });

  const fetchQueues = useCallback(async () => {
    try {
      const apiBase = await getApiBase();
      const tenantId = await getTenantId();
      const token = await getToken();
      const response = await fetch(`${apiBase}/v2/callcenter/queues`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': tenantId,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.queues)
            ? data.queues
            : Array.isArray(data?.data)
              ? data.data
              : (data && typeof data === 'object')
                ? Object.values(data)
                : [];
        setQueues(list);
      }
    } catch (e) {}
  }, []);

  const fetchCalls = useCallback(async (pageNum = 0) => {
    try {
      setLoading(true);
      const apiBase = await getApiBase();
      const tenantId = await getTenantId();
      const params = new URLSearchParams({
        limit: String(limit),
        offset: String(pageNum * limit),
      });
      
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.queue) params.append('queue_key', filters.queue);
      if (filters.agent) params.append('agent_key', filters.agent);
      if (filters.disposition) params.append('disposition', filters.disposition);

      const response = await fetch(`${apiBase}/v2/analytics/history/${tenantId}/calls?${params}`);

      if (response.ok) {
        const data = await response.json();
        setCalls(data.data || []);
        setTotal(data.total || 0);
        setPage(pageNum);
      }
    } catch (e) {
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const apiBase = await getApiBase();
      const tenantId = await getTenantId();

      const params = new URLSearchParams();
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.queue) params.append('queue_key', filters.queue);
      if (filters.agent) params.append('agent_key', filters.agent);
      if (filters.disposition) params.append('disposition', filters.disposition);

      const response = await fetch(`${apiBase}/v2/analytics/export/calls/${tenantId}?${params}`);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `chamadas_${tenantId}_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (e) {
      console.error('Erro ao exportar CSV:', e);
      alert('Erro ao exportar dados. Tente novamente.');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetchQueues();
  }, [fetchQueues]);

  useEffect(() => {
    fetchCalls(0);
  }, [fetchCalls]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCalls(0);
  };

  const handleClearFilters = () => {
    setFilters({
      startDate: weekAgo,
      endDate: today,
      queue: '',
      agent: '',
      disposition: '',
    });
  };

  const getDispositionBadge = (disposition) => {
    const config = {
      answered: { className: 'ccv2-badge--success', label: 'Atendida' },
      abandoned: { className: 'ccv2-badge--danger', label: 'Abandonada' },
      no_answer: { className: 'ccv2-badge--warn', label: 'Sem Resposta' },
      busy: { className: 'ccv2-badge--warn', label: 'Ocupado' },
      failed: { className: 'ccv2-badge--danger', label: 'Falha' },
    };
    const c = config[disposition] || { className: 'ccv2-badge', label: disposition || '-' };
    return (
      <span className={`ccv2-badge ${c.className}`}>
        {c.label}
      </span>
    );
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="ccv2-page-container">
      <div className="ccv2-page-header">
        <div className="ccv2-page-title-area">
          <h1>Historico de Chamadas</h1>
          <p>Consulte o historico detalhado de chamadas do Call Center</p>
        </div>
        <div className="ccv2-page-actions">
          <button 
            className="ccv2-btn ccv2-btn-secondary" 
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? 'Exportando...' : 'Exportar CSV'}
          </button>
        </div>
      </div>

      <form className="ccv2-filters" onSubmit={handleSearch}>
        <div className="ccv2-filter-group">
          <label>Data Inicio</label>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
          />
        </div>
        <div className="ccv2-filter-group">
          <label>Data Fim</label>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
          />
        </div>
        <div className="ccv2-filter-group">
          <label>Fila</label>
          <select
            value={filters.queue}
            onChange={(e) => setFilters({ ...filters, queue: e.target.value })}
          >
            <option value="">Todas</option>
            {queues.map((q) => (
              <option key={q.queueKey || q.queue_key} value={q.queueKey || q.queue_key}>
                {q.name || q.queueKey || q.queue_key}
              </option>
            ))}
          </select>
        </div>
        <div className="ccv2-filter-group">
          <label>Agente</label>
          <input
            type="text"
            placeholder="Interface do agente"
            value={filters.agent}
            onChange={(e) => setFilters({ ...filters, agent: e.target.value })}
          />
        </div>
        <div className="ccv2-filter-group">
          <label>Status</label>
          <select
            value={filters.disposition}
            onChange={(e) => setFilters({ ...filters, disposition: e.target.value })}
          >
            <option value="">Todos</option>
            <option value="answered">Atendidas</option>
            <option value="abandoned">Abandonadas</option>
            <option value="no_answer">Sem Resposta</option>
          </select>
        </div>
        <div className="ccv2-filter-actions">
          <button type="submit" className="ccv2-btn ccv2-btn-primary">
            Buscar
          </button>
          <button type="button" className="ccv2-btn ccv2-btn-outline" onClick={handleClearFilters}>
            Limpar
          </button>
        </div>
      </form>

      <div className="ccv2-results-info">
        <span>{total} chamada(s) encontrada(s)</span>
        {totalPages > 1 && (
          <span className="ccv2-pagination-info">
            Pagina {page + 1} de {totalPages}
          </span>
        )}
      </div>

      <div className="ccv2-config-card">
        {loading ? (
          <div className="ccv2-config-loading">
            <div className="ccv2-spinner"></div>
          </div>
        ) : (
          <>
            <div className="ccv2-config-table-wrapper">
              <table className="ccv2-config-table">
                <thead>
                  <tr>
                    <th>Data/Hora</th>
                    <th>Origem</th>
                    <th>Nome</th>
                    <th>Fila</th>
                    <th>Agente</th>
                    <th>Espera</th>
                    <th>Duração</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {calls.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="ccv2-empty-row">
                        Nenhuma chamada encontrada para os filtros selecionados
                      </td>
                    </tr>
                  ) : (
                    calls.map((call, idx) => (
                      <tr key={call.unique_id || idx}>
                        <td className="ccv2-cell-datetime">
                          <div>{formatDateTime(call.entered_at)}</div>
                          {call.answered_at && (
                            <small className="ccv2-cell-secondary">
                              Atendida: {formatTime(call.answered_at)}
                            </small>
                          )}
                        </td>
                        <td className="ccv2-cell-phone">{call.caller_number || '-'}</td>
                        <td>{call.caller_name || '-'}</td>
                        <td>{call.queue_name || '-'}</td>
                        <td>
                          <div>{call.agent_name || '-'}</div>
                          {(() => {
                            // Paired SIP+WebRTC: show ONE logical ramal as the
                            // primary label. The raw device that answered (with
                            // a "WebRTC" hint) lives in the tooltip.
                            const ramal =
                              call.agentExtension ||
                              call.agent_extension ||
                              extractExtension(call.agent_interface);
                            if (!ramal && !call.agent_interface) return null;
                            const web = isWebEndpoint(call.agent_interface);
                            return (
                              <small
                                className="ccv2-cell-secondary"
                                title={call.agent_interface || ''}
                              >
                                {ramal || call.agent_interface}
                                {web ? ' (WebRTC)' : ''}
                              </small>
                            );
                          })()}
                        </td>
                        <td className="ccv2-cell-time">{formatDuration(call.wait_time)}</td>
                        <td className="ccv2-cell-time">{formatDuration(call.talk_time)}</td>
                        <td>{getDispositionBadge(call.disposition)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="ccv2-pagination">
                <button
                  className="ccv2-btn ccv2-btn-sm"
                  disabled={page === 0}
                  onClick={() => fetchCalls(0)}
                >
                  Primeira
                </button>
                <button
                  className="ccv2-btn ccv2-btn-sm"
                  disabled={page === 0}
                  onClick={() => fetchCalls(page - 1)}
                >
                  Anterior
                </button>
                <span className="ccv2-page-number">{page + 1} / {totalPages}</span>
                <button
                  className="ccv2-btn ccv2-btn-sm"
                  disabled={page >= totalPages - 1}
                  onClick={() => fetchCalls(page + 1)}
                >
                  Proxima
                </button>
                <button
                  className="ccv2-btn ccv2-btn-sm"
                  disabled={page >= totalPages - 1}
                  onClick={() => fetchCalls(totalPages - 1)}
                >
                  Ultima
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default History;
